<template>
  <div>
    <el-dialog
      :visible.sync="modal.opened"
      :show-close="false"
      width="500px"
      :fullscreen="$mq === 'xs'">
      <div slot="title">
        <h4 class="title title-up">{{ modal.item.product_name }}</h4>
      </div>
      <p>{{$t(`Do you really want to ${modal.item.starts_in_future ? 'delete' : 'close'} the subscription?`)}}</p>
      <p>{{$t('Subscription will be recreated after changing options')}}</p>
      <template slot="footer">
        <el-button simple class="cancel-button" @click.native="modal.opened = false">{{ $t('No') }}</el-button>
        <el-button :loading="modal.loading" type="danger" @click.native="closeSubscription(modal.item)">{{ $t('Yes') }}</el-button>
      </template>
    </el-dialog>
    <div class="tools">
      <el-radio-group fill="#F46239" v-model="filterActive" @change="changeFilter" size="small">
        <el-radio-button class='filter-label' :label="1">{{$t('Active')}}</el-radio-button>
        <el-radio-button class='filter-label' :label="0">{{$t('Completed')}}</el-radio-button>
      </el-radio-group>
      <el-button v-if="allowCreate" type="primary" @click="$router.push({ name: 'createSubscription' })" size="small" round plain >{{$t('Create')}}</el-button>
    </div>
    <el-collapse v-loading="loading" v-if="mobileScreen" accordion>
      <el-collapse-item v-for="subscription in data" :key="subscription.subscription_id">
        <template slot="title">
          <div class="collapse-title">
            {{ subscription.product_name }}
            <el-tag :type="filterActive ? 'success' : 'warning'" style="border-radius: 20px" class="subscriptions-price" size="small"> {{ subscription.total_price + ' ' + subscription.cur_symbol }} </el-tag>
          </div>
        </template>
        <ul style="list-style: none; padding-left: 0">
          <li v-for="(value, key, index) in subscriptionProps" :key="index">
            <ul v-if="value === 'services'" style="list-style: none; padding-left: 0">
              <strong >{{$t(key)}}:</strong>
              <li class="service" v-for="service in subscription[value]" :key="service.service_id">
                <span>{{ service.name }}, {{ service.mul }}{{$t(service.unit_name)}}</span>
                <el-tag type="success" class="price" size="mini">{{ service.cost + subscription.cur_symbol }}</el-tag>
              </li>
            </ul>
            <span v-else>
              <template v-if="value !== 'total_price'">
                <strong>{{$t(key)}}:</strong> {{ subscription[value] }}
              </template>
            </span>
          </li>
        </ul>
        <el-button-group v-if="subscription.state !== 3">
          <el-button v-if="allowEdit" @click.prevent.stop="editProduct(subscription)" type="info" icon="el-icon-edit" size="mini">{{ $t('Edit') }}</el-button>
          <el-button v-if="allowStop" @click.prevent.stop="confirmDelete(subscription)" type="danger" icon="el-icon-delete" size="mini">{{ $t('Delete') }}</el-button>
        </el-button-group>
      </el-collapse-item>
    </el-collapse>
    <el-table
      v-else
      :data="data"
      @row-click="expandRow"
      v-loading="loading"
      class="subscriptions-table"
      ref="subscriptions"
      @sort-change="sortItems"
      :default-sort = "{ prop: 'timefrom', order: 'descending' }"
      :row-style="{ 'cursor': 'pointer' }">
      <el-table-column type="expand" width="20px">
        <template slot-scope="scope">
          <ul class="services-list">
            <li class="service" v-for="service in scope.row.services" :key="service.service_id">
              <span>{{service.name}}, {{ service.mul+$t(service.unit_name) }}</span>
              <el-tag type="success" class="price" size="mini">{{ optionPrice(service, scope.row.cur_symbol) }}</el-tag>
            </li>
          </ul>
        </template>
      </el-table-column>
      <el-table-column prop="product_name" :label="$t('Title')" sortable="custom" align="left">
        <template slot-scope="scope">
          <el-tooltip :content="processedTooltip(scope.row)" :open-delay="300" placement="top">
            <i :class="{ 'el-icon-time': scope.row.state <= 0, 'el-icon-lock':  scope.row.current_blocking}" style="margin-right: 10px" v-if="scope.row.state <= 0 || scope.row.current_blocking"></i>
          </el-tooltip>
          <el-tooltip :content="plannedChangesTooltip(scope.row.global_next_change_date)" :open-delay="300" placement="top">
            <i class="el-icon-date" style="margin-right: 5px; font-size: 14px;" v-if="hasPlannedChanges(scope.row)"></i>
          </el-tooltip>
          <span>{{ scope.row.descr ? scope.row.descr : scope.row.product_name }}</span>
          <a v-if="scope.row.product_link" @click.stop :href="scope.row.product_link" target="_blank" rel="noopener noreferrer">
            <el-tooltip content="Подробная информация" :open-delay="300" placement="top">
              <i class="el-icon-info" style="margin-left: 10px; color: #9A9A9A"></i>
            </el-tooltip>
          </a>
          <div v-if="scope.row.descr" style="color: #9A9A9A;">{{ scope.row.product_name }}</div>
        </template>
      </el-table-column>
      <el-table-column prop="timefrom" :label="$t('Date')" sortable="custom" align="center">
        <template slot-scope="scope">
          {{ getTimefrom(scope.row.global_start_date) }}
        </template>
      </el-table-column>
      <el-table-column prop="agrm_num" :label="$t('Agreement')" align="center" sortable="custom">
        <template slot-scope="scope">
          {{ scope.row.is_offer_agrm ? $t('Offer contract') : scope.row.agrm_num }}
        </template>
      </el-table-column>
      <el-table-column prop="service_count" :label="$t('Services')" align="center" sortable="custom" width="150">
        <template slot-scope="scope">
          <el-button v-if="scope.row.service_count" type="text" style="outline: 0">
            {{ scope.row.service_count + $t('pc') }}
          </el-button>
          <span v-else>—</span>
        </template>
      </el-table-column>
      <el-table-column prop="period_id" :label="$t('Period')" align="center" sortable="custom">
        <template slot-scope="scope">
            {{ getPeriodString(scope.row.period_type, scope.row.period_length) }}
        </template>
      </el-table-column>
      <el-table-column prop="monthly_cost" :label="$t('Cost')" align="right" sortable="custom">
        <template slot-scope="scope">
            {{ `${formatPrice(scope.row.monthly_cost)} ${scope.row.cur_symbol}` }}
        </template>
      </el-table-column>
      <el-table-column
        v-if="allowEdit || allowStop ? filterActive : false"
        width="100"
        align="right">
        <template slot-scope="scope">
          <el-tooltip :content="$t('Edit')" :open-delay="300" placement="top">
            <el-button v-if="allowEdit && !hasPlannedChanges(scope.row)" :disabled="editIsDisabled(scope.row)" size="small" @click.prevent.stop="editProduct(scope.row)" icon="el-icon-edit" circle></el-button>
          </el-tooltip>
          <el-tooltip :content="$t('Open scheduled version')" :open-delay="300" placement="top">
            <el-button v-if="hasPlannedChanges(scope.row)" size="small" @click.prevent="showPlannedChanges(scope.row)" icon="el-icon-date" circle></el-button>
          </el-tooltip>
          <el-tooltip :content="$t(scope.row.starts_in_future ? 'Delete' : 'Terminate')" :open-delay="300" placement="top">
            <el-button v-if="allowStop" :disabled="deleteIsDisabled(scope.row)" size="small" @click.prevent.stop="confirmDelete(scope.row)" :icon="scope.row.starts_in_future ? 'el-icon-delete' : 'el-icon-switch-button'" type="danger" plain circle></el-button>
          </el-tooltip>
        </template>
      </el-table-column>
      <div slot="empty">
        {{ $t('No active subscriptions')}}
      </div>
      <div v-if="filterActive" slot="append" class="total-row" >
        <span>{{ $t('Total') }}</span>
        <ul class="prices-list">
          <li v-for="(sum, period) in totalPriceGroupedByPeriod(data)" :key="period">
            {{ `${sum} ${data[0].cur_symbol} / ${period}` }}
          </li>
        </ul>
      </div>
    </el-table>
  </div>
</template>

<script>
import { Table, TableColumn, Button, ButtonGroup, RadioGroup, RadioButton, Tag, Collapse, CollapseItem } from 'element-ui'
import { mapState } from 'vuex'
import axios from 'axios'
import { format, isValid, subDays, addDays } from 'date-fns'
import number from './mixins/number'
import { chargingPeriods } from './mixins/chargingPeriods'

export default {
  name: 'SubscriptionsList',
  mixins: [number, chargingPeriods],
  props: {
    data: {
      type: Array,
      required: true
    },
    loading: {
      type: Boolean
    },
    agrmid: {
      type: Number
    }
  },
  components: {
    'el-table': Table,
    'el-table-column': TableColumn,
    'el-button': Button,
    'el-button-group': ButtonGroup,
    'el-radio-group': RadioGroup,
    'el-tag': Tag,
    'el-radio-button': RadioButton,
    'el-collapse': Collapse,
    'el-collapse-item': CollapseItem
  },
  data () {
    return {
      activeServices: [],
      modal: {
        item: {},
        opened: false,
        loading: false
      },
      filterActive: 1,
      window: {
        width: 0,
        height: 0
      },
      selectedRow: null,
      subscriptionProps: {
        'Title': 'product_name',
        'Date': 'timefrom',
        'Agreement': 'agrm_num',
        'Count of services': 'service_count',
        'Cost': 'total_price',
        'Period': 'period_id',
        'Services': 'services'
      }
    }
  },
  methods: {
    changeFilter: function(value) {
      this.$emit('changeFilter', this.filterActive)
      this.selectedRow = null
      this.$refs.subscriptions.setCurrentRow()
    },
    goToProducts: function() {
      this.$router.push({
        name: 'SubscriptionsConstructor',
        params: {
          agrmid: this.agrmid
        }
      })
    },
    hasPlannedChanges: function(subscription) {
      const changeDate = new Date(subscription.global_next_change_date)
      return isValid(changeDate) && subscription.global_next_change_date !== null
    },
    plannedChangesTooltip: function(date) {
      const nextChangeDate = new Date(date)
      return this.$t('Changes planned from {date}', { date: format(nextChangeDate, 'dd.MM.yyyy') })
    },
    getTimefrom: function(timefrom) {
      return format(new Date(timefrom), 'dd.MM.yyyy')
    },
    editIsDisabled: function(subscription) {
      if (subscription.state <= 0 && !subscription.starts_in_future) {
        return true
      }
      if (subscription.pc_unavailable === 1) {
        return true
      }
      if (this.hasPlannedChanges(subscription)) {
        return true
      }
      if (subscription.timeto && subscription.timeto !== '9999-12-31 23:59:59' && !subscription.starts_in_future) {
        return true
      }

      return false
    },
    deleteIsDisabled: function(subscription) {
      if (subscription.ends_today) {
        return true
      }
      if (subscription.state <= 0 && !subscription.starts_in_future) {
        return true
      }
      if (subscription.pc_unavailable) {
        return true
      }

      return false
    },
    processedTooltip: function (subscription) {
      if (subscription.current_blocking) {
        return this.$t(subscription.current_blocking === 3 ? 'Subscription has been blocked by operator' : 'Not enough funds for subscription')
      }
      return subscription.starts_in_future ? this.$t('Will start on') + ' ' + subscription.timefrom : this.$t('Processing')
    },
    editProduct: function(subscription) {
      let timefrom = new Date(subscription.timefrom)
      let minTimefrom = new Date(subscription.timefrom)
      if (subscription.starts_today || subscription.starts_in_past) {
        timefrom = new Date()
        minTimefrom = subDays(new Date(), 1)
      }
      if (subscription.starts_in_future) {
        minTimefrom = subDays(new Date(), 1)
      }
      console.log('timefrom', subscription.timefrom)
      this.$router.push({
        name: 'editSubscription',
        params: {
          editing: true,
          productId: subscription.product_id,
          periodId: subscription.period_id,
          agrmid: subscription.agrm_id,
          subscriptionId: subscription.subscription_id,
          timefrom: timefrom,
          minTimefrom: minTimefrom,
          globalId: subscription.global_id,
          timeto: subscription.timeto,
          starts_in_future: subscription.starts_in_future,
          starts_today: subscription.starts_today
        }
      })
    },
    showPlannedChanges: function(subscription) {
      let minTimefrom = new Date()
      if (this.$route.params.starts_today) {
        minTimefrom = addDays(minTimefrom, 1)
      }
      this.$router.push({
        name: 'editSubscription',
        params: {
          editing: true,
          productId: subscription.product_id,
          agrmid: subscription.agrm_id,
          subscriptionId: subscription.next_subscription_id,
          globalId: subscription.global_id,
          timefrom: new Date(subscription.global_next_change_date),
          minTimefrom: minTimefrom,
          timeto: subscription.timeto,
          starts_in_future: subscription.starts_in_future,
          starts_today: subscription.starts_today,
          showPlannedChanges: true
        }
      })
    },
    confirmDelete: function(subscription) {
      this.modal.item = subscription
      this.modal.opened = true
    },
    closeSubscription: function(item) {
      this.modal.loading = true
      axios.delete('subscriptions/' + item.subscription_id, { data: {
        starts_in_future: item.starts_in_future
      } }).then(promise => {
        this.modal.loading = false
        this.modal.opened = false
        this.$emit('subscriptionDeleted')
        this.$notify(
          {
            message: 'Подписка остановлена',
            horizontalAlign: 'center',
            verticalAlign: 'top',
            type: 'success',
            timeout: 1500
          })
      }).catch(error => {
        this.modal.opened = false
        this.modal.loading = false
        this.$notify(
          {
            message: error.response.data.error,
            horizontalAlign: 'center',
            verticalAlign: 'top',
            type: 'danger'
          })
      })
    },
    sortItems: function(param) {
      this.$emit('sort', {
        column: param.prop,
        order: param.order,
        filterActive: this.filterActive
      })
    },
    getSummary(param) {
      const { columns, data } = param
      const sums = []
      console.log('data', data)
      columns.forEach((column, index) => {
        const values = data.map(item => Number(item[column.property]))
        if (!values.every(value => isNaN(value)) && column.property === 'monthly_cost') {
          console.log('values', values)
          sums[index] = values.reduce((prev, curr) => {
            const value = Number(curr)
            if (!isNaN(value)) {
              return prev + curr
            } else {
              return prev
            }
          }, 0)
          sums[index] = this.$t('Sum') + ' ' + this.formatPrice(sums[index]) + ' ' + data[0].cur_symbol + '/' + this.$t('mo')
        }
      })
      return sums
    },
    expandRow (row) {
      this.$refs.subscriptions.toggleRowExpansion(row)
    },
    handleResize() {
      this.window.width = window.innerWidth
      this.window.height = window.innerHeight
    },
    rowStyle() {
      return {
        'cursor': 'pointer'
      }
    },
    optionPrice: function(service, currency = 'руб') {
      let periodString = ''
      const unitString = service.bulk_service ? `${this.$t('for')} ${this.$t('unit')}` : ''
      if (service.rent_period === 0) {
        periodString = this.$t('once')
      } else if (service.bulk_service) {
        periodString = ''
      } else {
        periodString = `${this.$t('for')} ${this.$t('Period').toLowerCase()}`
      }
      return `${service.cost} ${currency} ${periodString} ${unitString}`
    }
  },
  computed: {
    ...mapState({
      subscriptionsMenu: state => state.config.data.menu.subscriptions,
      allowCreate: state => state.config.data.allowCreateSubscription,
      allowEdit: state => state.config.data.allowEditSubscription,
      allowStop: state => state.config.data.allowStopSubscription
    }),
    servicesArray: function() {
      return this.data
    },
    mobileScreen: function() {
      if (this.window.width < 768) {
        return true
      } else {
        return false
      }
    },
    mediumScreen: function() {
      if (this.window.width >= 768) {
        return true
      } else {
        return false
      }
    },
    largeScreen: function() {
      if (this.window.width >= 992) {
        return true
      } else {
        return false
      }
    },
    extraLargeScreen: function() {
      if (this.window.width >= 1200) {
        return true
      } else {
        return false
      }
    }
  },
  created() {
    window.addEventListener('resize', this.handleResize)
    this.handleResize()
  },
  destroyed() {
    window.removeEventListener('resize', this.handleResize)
  }
}
</script>

<style lang="scss" scoped>
.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-left: 0 !important;
  padding-right: 0 !important;
}
h4 {
  margin: 0;
}
.filter-label {
  font-size: 14px;
  margin-bottom: 0;
}
.new-button {
  margin: 0
}
.subscriptions-table >>> .el-table__footer {
  font-weight: bold !important;
}

.services-list {
  list-style: none;
  padding-left: 0;
  @media (min-width: 1200px) {
    width: 33%;
  }
}

.service {
  display: flex;
  justify-content: space-between;
  margin-bottom: 5px;
  @media (min-width: 768px) {
    &:not(:last-child) {
      margin-bottom: 15px;
    }
  }
}

.price {
  margin-left: 30px;
  border-radius: 20px;
  padding-left: 10px;
  padding-right: 10px;
}
.tools {
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
  margin-top: 20px;
  @media (min-width: 768px) {
    margin-bottom: 0;
  }
}
.check {
  margin-bottom: 0;
}
.action-delete {
  border-left-color: #d3d4d6 !important;
  .is-disabled {
    border-left-color: #fde2e2 !important;
  }
}
.action-edit {
  border-right-style: none !important;
}

.collapse-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.subscriptions-price {
  margin: 0;
  margin-right: 10px;
}
.total-row {
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  padding: 1rem 10px
}
.prices-list {
  list-style: none;
  margin-bottom: 0;
}
</style>
